import useSWRInfinite from "swr/infinite";
import { useCallback } from "react";
import { NovelUseCase } from "@/usecases/novelUseCase";
import { useAuth } from "@/hooks/useAuth";
import {
  convertSeriesResponseToISeriesListItemOrUndefined,
  type ISeriesListItem,
} from "@/models/seriesListItem";
import { filterUndefined } from "@/utils/filter-undefined";

type Data = {
  items: ISeriesListItem[];
  nextCursor?: string;
  hasNext: boolean;
};

export const useFetchMySeriesList = () => {
  const { tellerUser } = useAuth();

  const getKey = useCallback(
    (_: number, prevData: Data | undefined) => {
      if (!tellerUser?.id?.value) {
        return null;
      }
      if (!prevData) {
        return ["/api/user/novels", ""];
      }
      if (prevData.nextCursor) {
        return ["/api/user/novels", prevData.nextCursor];
      }
      return null;
    }, // SWR key
    [tellerUser],
  );

  const fetcher = useCallback(async ([_key, cursor]: [string, string]) => {
    const novelsResult = await new NovelUseCase().fetchUserNovels(cursor);

    const items =
      novelsResult?.seriesList
        ?.map(convertSeriesResponseToISeriesListItemOrUndefined)
        ?.filter(filterUndefined) ?? [];

    const hasNextPage = novelsResult?.cursor?.hasNextPage?.value ?? false;
    const nextCursor = novelsResult?.cursor?.endCursor?.value ?? undefined;

    return {
      items: items,
      nextCursor: nextCursor,
      hasNext: hasNextPage,
    };
  }, []);

  return useSWRInfinite(getKey, fetcher);
};
